import _ from 'lodash'
import React, { useContext, useState, useEffect } from 'react'
import { hooks } from '@adc/ui-core'
import PropTypes from 'prop-types'
import coreLogger from '@adc/logger'
// TODO; remove this once we're sure all usages of old notifier are done away with
import { NullNotifier, NotifierPropTypes } from './notifiers'
import { primitives } from '@adc/blueprint'
import canUseDom from '../../util/canUseDom'
import { NUDGE_CONSTANTS } from '../../util/investorPreferences'

export const ApplicationContextShape = {
  // Global indicator for whether async work is currently happening
  isCurrentlyFetching: false,

  // Global indicator for whether our apps should show they are in maintenance mode. This has been used in the past to
  // communicate during scheduled maintenance windows, or when performance degrades enough to warrant informing the user
  // somehow without bringing the whole site offline.
  //
  // TODO: Hook this up with the maintenance message we show today based on feature flags
  isMaintenanceEnabled: false,

  // Logger specific for this application
  logger: coreLogger.forModule('AUCTIONCOM'),

  // this will store either browser or webview to indicate whether the app is running within a  web browser or part of the mobile app
  embeddedContext: '',

  // Single instance of a notification system that all components in this application should use by default.
  notifier: new NullNotifier(),
  graphWebsocket: new primitives.NullGraphWebsocket(),
  // TODO: Can we make this a bitset, which makes it trivial to ensure only a single bit is every flipped, which
  // translates into only ever showing a single modal. If we ever create a stacking context with multiple modals, then
  // that is really a bug.
  modalState: '',
  setModalState: () => {},
  featureFlags: {},
  sspConfig: {},
  user: {},
  auth: {},
  notifications: {
    data: [],
    pagination: {
      page: 0,
      take: 20,
      totalPages: null,
      totalElements: null,
    },
  },
  capabilities: {
    authentication: {
      enabled: true,
      options: {
        onAuthenticationComplete: _.noop,
        onOnboardingComplete: _.noop,
      },
    },
  },
  applicationSessionInformation: {},
  experiments: {},
}

// TODO: Should I export this? Can't see a need for it directly now. And I export the consumer and provider down below
// to do the same thing.
export const ApplicationContext = React.createContext(ApplicationContextShape)
export const ApplicationContextConsumer = ApplicationContext.Consumer

export const ApplicationContextProvider = props => {
  const { children } = props
  const currentContext = ApplicationContext._currentValue

  // The logic below is structured as it is because we don’t want valid properties in the props object to be overwritten
  // by empty ones in the ApplicationContextShape (which is why we can't simply spread the objects together). isNil is
  // needed because there are valid false cases - see `isMaintenanceEnabled` as one example -- so it’s needed to
  // differentiate between undefined/null and false to correctly merge the objects without losing the values we want.
  const {
    sspConfig,
    user,
    auth,
    featureFlags,
    logger,
    notifier,
    graphWebsocket,
    capabilities,
    experiments,
    embeddedContext,
  } = Object.keys(ApplicationContextShape).reduce((acc, key) => {
    const propsValue = props[key]
    const currentContextValue = currentContext[key]
    const defaultContextValue = ApplicationContextShape[key]

    // NOTE: If we ever explicitly want null passed in here we would need to change this condition to check for
    // `undefined `
    acc[key] =
      [propsValue, currentContextValue].find(v => !_.isNil(v)) ||
      defaultContextValue

    return acc
  }, {})

  const [isCurrentlyFetching, setIsFetching] = useState(false)

  const [modalState, setModalState] = useState('')
  const [loginOrSignup, setLoginOrSignup] = useState('login')

  const [notifications, setNotifications] = useState(
    ApplicationContextShape.notifications
  )

  const [localUserData, setLocalUserData] = useState({})

  const updateUser = updatedData => {
    setLocalUserData(currentData => _.merge({}, currentData, updatedData))
  }

  const onOnboardingComplete = () => {
    const updatedOnboardingAnswers =
      NUDGE_CONSTANTS().REQUIRED_QUESTION_CODES.map(value => ({
        question_code: value,
      }))
    updateUser({ onboarding_answers: updatedOnboardingAnswers })
  }

  const wasLoggedIn = hooks.usePrevious(user.logged_in, true)

  useEffect(() => {
    if (user.logged_in && !wasLoggedIn) {
      graphWebsocket.restart()
    }
  }, [user.logged_in, graphWebsocket, wasLoggedIn])

  hooks.useMountEffect(() => () => graphWebsocket.close())

  const providerValues = {
    isCurrentlyFetching,
    setIsFetching,
    sspConfig,
    user: _.merge({}, user, localUserData),
    auth,
    featureFlags,
    logger,
    modalState,
    setModalState,
    loginOrSignup,
    setLoginOrSignup,
    notifications, // TODO: deprecate, use useApplicationNotifications instead
    setNotifications, // TODO: deprecate, use useApplicationNotifications instead
    graphWebsocket,
    notifier,
    capabilities,
    actions: {
      updateUser,
      onOnboardingComplete,
    },
    experiments,
    embeddedContext,
  }

  if (embeddedContext) {
    // when passed in (e.g. from stories or test)
    providerValues.embeddedContext = embeddedContext
  } else if (
    // otherwise detect from the URL - this is for real-time mobile webview detection
    canUseDom() &&
    new URLSearchParams(window.location.search).get('deviceContext') ===
      'webview'
  ) {
    providerValues.embeddedContext = 'webview'
  } else {
    // default
    providerValues.embeddedContext = 'browser'
  }

  return (
    <ApplicationContext.Provider value={providerValues}>
      {children}
    </ApplicationContext.Provider>
  )
}

/* eslint-disable react/no-unused-prop-types */
ApplicationContextProvider.propTypes = {
  sspConfig: PropTypes.object,
  user: PropTypes.object,
  auth: PropTypes.object,
  featureFlags: PropTypes.object,
  children: PropTypes.any,
  notifier: NotifierPropTypes,

  // TODO: Create logging interface for this like we did for the notifiers.
  logger: PropTypes.object,
  experiments: PropTypes.object,
}
/* eslint-enable react/no-unused-prop-types */

// For use with function components using hooks
export const useApplicationContext = () => useContext(ApplicationContext)

// A lot of our components are not in a state where we can drop in hooks, so defining this
// HoC here for use with existing code.
export const withApplicationContext = Component => props =>
  (
    <ApplicationContextConsumer>
      {context => <Component applicationContext={context} {...props} />}
    </ApplicationContextConsumer>
  )
